/**
 * RetentionCurveChart Component
 * 
 * Displays retention curves for the most recent 6 cohorts.
 * Uses existing LineChart wrapper component.
 * 
 * X-axis: Time since signup (weeks/months)
 * Y-axis: Retention rate (percentage)
 * 
 * Requirements: 4.11, 4.12
 */

import React from 'react';
import PropTypes from 'prop-types';
import LineChart from './LineChart';
import './RetentionCurveChart.css';

const RetentionCurveChart = ({
  cohorts = [],
  loading = false,
  groupBy = 'month'
}) => {
  /**
   * Generate distinct colors for cohort lines
   * @param {number} index - Cohort index
   * @param {number} total - Total number of cohorts
   * @returns {string} Color hex code
   */
  const getCohortColor = (index, total) => {
    const colors = [
      '#3b82f6', // Blue
      '#10b981', // Green
      '#f59e0b', // Amber
      '#ef4444', // Red
      '#8b5cf6', // Purple
      '#ec4899'  // Pink
    ];
    return colors[index % colors.length];
  };

  /**
   * Transform cohort data for LineChart component
   * @returns {Object} Chart data in LineChart format
   */
  const transformDataForChart = () => {
    if (!cohorts || cohorts.length === 0) {
      return null;
    }

    // Take most recent 6 cohorts
    const recentCohorts = cohorts.slice(0, 6);

    // Define time intervals
    const intervals = [
      { key: 'week1', label: 'Week 1', value: 1 },
      { key: 'week2', label: 'Week 2', value: 2 },
      { key: 'week4', label: 'Week 4', value: 4 },
      { key: 'month2', label: 'Month 2', value: 8 },
      { key: 'month3', label: 'Month 3', value: 12 },
      { key: 'month6', label: 'Month 6', value: 24 }
    ];

    // Create labels (X-axis)
    const labels = intervals.map(interval => interval.label);

    // Create datasets (one per cohort)
    const datasets = recentCohorts.map((cohort, index) => {
      const data = intervals.map(interval => {
        const rate = cohort.retention[interval.key];
        return rate !== null ? rate : null;
      });

      return {
        label: cohort.name,
        data: data,
        borderColor: getCohortColor(index, recentCohorts.length),
        backgroundColor: getCohortColor(index, recentCohorts.length),
        borderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        tension: 0.3, // Smooth curves
        spanGaps: false // Don't connect null values
      };
    });

    return {
      labels,
      datasets
    };
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: function(context) {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            return value !== null ? `${label}: ${value.toFixed(1)}%` : `${label}: No data`;
          }
        }
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Time Since Signup',
          font: {
            size: 13,
            weight: 'bold'
          }
        },
        grid: {
          display: false
        }
      },
      y: {
        title: {
          display: true,
          text: 'Retention Rate (%)',
          font: {
            size: 13,
            weight: 'bold'
          }
        },
        min: 0,
        max: 100,
        ticks: {
          callback: function(value) {
            return value + '%';
          }
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        }
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="retention-curve-chart retention-curve-chart--loading">
        <div className="retention-curve-chart__loading">
          <div className="retention-curve-chart__spinner"></div>
          <p>Loading retention curves...</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (!cohorts || cohorts.length === 0) {
    return (
      <div className="retention-curve-chart retention-curve-chart--empty">
        <div className="retention-curve-chart__empty">
          <p>No cohort data available</p>
          <p className="retention-curve-chart__empty-hint">
            Retention curves will appear as cohorts accumulate activity data.
          </p>
        </div>
      </div>
    );
  }

  const chartData = transformDataForChart();

  if (!chartData) {
    return (
      <div className="retention-curve-chart retention-curve-chart--empty">
        <div className="retention-curve-chart__empty">
          <p>Unable to generate retention curves</p>
        </div>
      </div>
    );
  }

  return (
    <div className="retention-curve-chart">
      <div className="retention-curve-chart__header">
        <h3 className="retention-curve-chart__title">
          Retention Curves (Most Recent 6 Cohorts)
        </h3>
        <p className="retention-curve-chart__subtitle">
          Tracking retention rates over time for each cohort
        </p>
      </div>

      <div className="retention-curve-chart__container">
        <LineChart
          data={chartData}
          options={chartOptions}
          height={400}
        />
      </div>

      <div className="retention-curve-chart__info">
        <p className="retention-curve-chart__info-text">
          <strong>How to read:</strong> Each line represents a cohort. 
          Higher lines indicate better retention. 
          Gaps in lines indicate insufficient time has passed for that measurement.
        </p>
      </div>
    </div>
  );
};

RetentionCurveChart.propTypes = {
  cohorts: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    size: PropTypes.number.isRequired,
    retention: PropTypes.shape({
      week1: PropTypes.number,
      week2: PropTypes.number,
      week4: PropTypes.number,
      month2: PropTypes.number,
      month3: PropTypes.number,
      month6: PropTypes.number
    }).isRequired
  })),
  loading: PropTypes.bool,
  groupBy: PropTypes.oneOf(['week', 'month'])
};

export default RetentionCurveChart;
